import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { getDoctorAccess, submitCase } from '../services/api/doctorAccess';
import type { DoctorInfo } from '../services/api/doctorAccess';
import { usePrescriptionForm } from '../hooks/formHooks/usePrescriptionForm';
import type { PrescriptionFormData } from '../hooks/formHooks/usePrescriptionForm';
import type { Patient } from '../types';

// ─── Page-level state machine ─────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'expired' | 'ready';
type SubmitState = 'idle' | 'submitting' | 'submitted' | 'error';

// ─── Countdown hook ───────────────────────────────────────────────────────────

function useCountdown(initialMinutes: number | null): number | null {
  const [secs, setSecs] = useState<number | null>(
    initialMinutes !== null ? Math.round(initialMinutes * 60) : null,
  );
  useEffect(() => {
    if (initialMinutes === null) return;
    setSecs(Math.round(initialMinutes * 60));
    const id = setInterval(
      () => setSecs((s) => (s !== null ? Math.max(0, s - 1) : null)),
      1000,
    );
    return () => clearInterval(id);
  }, [initialMinutes]);
  return secs;
}

function fmtCountdown(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ─── Shared primitives ────────────────────────────────────────────────────────

const inputCls = (err?: string) =>
  `w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${
    err
      ? 'border-red-400 focus:ring-red-300 bg-red-50 dark:bg-red-900/10'
      : 'border-gray-200 dark:border-gray-700 focus:ring-blue-400 bg-white dark:bg-gray-800'
  } text-gray-900 dark:text-white placeholder-gray-400`;

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ label, error, required, hint, className = '', children }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />{error}
      </p>
    )}
  </div>
);

interface SectionProps { title: string; icon: React.ReactNode; children: React.ReactNode; accent?: 'blue' | 'emerald'; }
const Section: React.FC<SectionProps> = ({ title, icon, children, accent = 'blue' }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <span className={`text-sm ${accent === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400'}`}>{icon}</span>
      <h2 className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">{title}</h2>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const YesNoToggle: React.FC<{ value: 'Yes' | 'No'; onChange: (v: 'Yes' | 'No') => void }> = ({ value, onChange }) => (
  <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-fit flex-shrink-0">
    {(['Yes', 'No'] as const).map((opt) => (
      <button key={opt} type="button" onClick={() => onChange(opt)}
        className={`px-4 py-2 text-sm font-semibold transition-colors ${
          value === opt
            ? opt === 'Yes' ? 'bg-emerald-500 text-white' : 'bg-red-400 text-white'
            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}>
        {opt}
      </button>
    ))}
  </div>
);

const HistoryRow: React.FC<{
  label: string; value: 'Yes' | 'No'; onToggle: (v: 'Yes' | 'No') => void;
  detailName: string; detailValue: string;
  onDetailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  detailPlaceholder?: string;
}> = ({ label, value, onToggle, detailName, detailValue, onDetailChange, detailPlaceholder }) => (
  <div className="flex flex-wrap items-center gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <span className="text-sm text-gray-700 dark:text-gray-200 flex-1 min-w-[200px]">{label}</span>
    <YesNoToggle value={value} onChange={onToggle} />
    {value === 'Yes' && (
      <input name={detailName} value={detailValue} onChange={onDetailChange}
        placeholder={detailPlaceholder || 'Specify details…'}
        className="flex-1 min-w-[160px] px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400" />
    )}
  </div>
);

// ─── Status styles ────────────────────────────────────────────────────────────

// const STATUS_STYLES: Record<string, string> = {
//   pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
//   assigned: 'bg-blue-100   text-blue-800   dark:bg-blue-900/30   dark:text-blue-300',
//   approved: 'bg-green-100  text-green-800  dark:bg-green-900/30  dark:text-green-300',
//   rejected: 'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-300',
//   issued:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
// };

// ─── PatientDetails (read-only) ───────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
  if (!value && value !== '0') return null;
  return (
    <div className="flex gap-3 py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 w-40 flex-shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm text-gray-800 dark:text-gray-100 flex-1 break-words">{value}</dd>
    </div>
  );
};

const PatientDetails: React.FC<{ patient: Patient }> = ({ patient }) => {
  const p = patient as any;
  return (
    <div className="space-y-4">
      <Section title="Identity" icon={<IdentificationIcon className="w-4 h-4" />}>
        <dl>
          <InfoRow label="Full Name" value={patient.full_name || [patient.name, patient.last_name].filter(Boolean).join(' ')} />
          <InfoRow label="Age" value={patient.age} />
          <InfoRow label="Gender" value={patient.gender} />
          <InfoRow label="C/O (Care of)" value={patient.care_of} />
          <InfoRow label="Identification Mark" value={patient.id_mark} />
        </dl>
      </Section>

      <Section title="Address" icon={<DocumentTextIcon className="w-4 h-4" />}>
        <dl>
          <InfoRow label="Address" value={patient.full_address} />
          <InfoRow label="Street" value={p.street} />
          <InfoRow label="Address Line 2" value={p.address_line_2} />
          <InfoRow label="City" value={patient.city} />
          <InfoRow label="State" value={patient.state} />
          <InfoRow label="Zip Code" value={p.zip_code} />
          <InfoRow label="Country" value={p.country} />
        </dl>
      </Section>

      <Section title="Case & Medical" icon={<ClipboardDocumentCheckIcon className="w-4 h-4" />}>
        <dl>
          <InfoRow label="Doc ID" value={patient.doc_id} />
          <InfoRow label="Certificate Type" value={patient.certificate_type} />
          <InfoRow label="Organisation" value={patient.organisation} />
          <InfoRow label="Diagnosis" value={patient.diagnosis} />
          <InfoRow label="Detail of Illness" value={patient.detail_of_illness} />
          <InfoRow label="Exam Time" value={p.exam_time} />
          <InfoRow label="From Date" value={patient.from_date} />
          <InfoRow label="No. of Days" value={patient.no_of_days} />
          <InfoRow label="Additional Details" value={patient.additional_details} />
          <InfoRow label="Status" value={patient.status} />
        </dl>
      </Section>

      {(patient.caretaker_full_name || p.caretaker_relation) && (
        <Section title="Caretaker" icon={<UserCircleIcon className="w-4 h-4" />}>
          <dl>
            <InfoRow label="Full Name" value={patient.caretaker_full_name} />
            <InfoRow label="Age" value={patient.caretaker_age} />
            <InfoRow label="Gender" value={p.caretaker_gender} />
            <InfoRow label="Relation" value={p.caretaker_relation} />
          </dl>
        </Section>
      )}
    </div>
  );
};

// ─── CaseList ─────────────────────────────────────────────────────────────────

// const CaseList: React.FC<{
//   patients: Patient[];
//   submittedIds: Set<string>;
//   onSelect: (p: Patient) => void;
// }> = ({ patients, submittedIds, onSelect }) => {
//   if (patients.length === 0) {
//     return (
//       <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 px-6 py-12 text-center text-sm text-gray-400">
//         No cases assigned to you.
//       </div>
//     );
//   }
//   return (
//     <div className="space-y-3">
//       {patients.map((p) => {
//         const name = p.full_name || [p.name, p.last_name].filter(Boolean).join(' ') || '—';
//         const submitted = submittedIds.has(p.doc_id);
//         return (
//           <button key={p.doc_id} onClick={() => onSelect(p)}
//             className="w-full text-left bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md transition-all px-5 py-4">
//             <div className="flex items-start justify-between gap-3">
//               <div className="flex-1 min-w-0">
//                 <p className="font-semibold text-gray-900 dark:text-white truncate">{name}</p>
//                 <p className="text-xs text-gray-400 mt-0.5">
//                   {[p.age ? `${p.age}y` : null, p.gender, p.certificate_type].filter(Boolean).join(' · ')}
//                 </p>
//                 {p.doc_id && <p className="text-xs text-gray-400 font-mono mt-0.5">#{p.doc_id}</p>}
//               </div>
//               <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
//                 {submitted ? (
//                   <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
//                     <CheckCircleIcon className="w-3 h-3" /> Submitted ✓
//                   </span>
//                 ) : (
//                   <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-700'}`}>
//                     {p.status}
//                   </span>
//                 )}
//                 <span className="text-xs text-blue-600 dark:text-blue-400">
//                   {submitted ? 'View →' : 'Fill Prescription →'}
//                 </span>
//               </div>
//             </div>
//           </button>
//         );
//       })}
//     </div>
//   );
// };

// ─── StepBar ──────────────────────────────────────────────────────────────────

const STEP_LABELS = ['Patient Info', 'Prescription', 'Review'];

const StepBar: React.FC<{ current: number }> = ({ current }) => (
  <div className="flex items-center">
    {STEP_LABELS.map((label, i) => (
      <React.Fragment key={i}>
        <div className="flex flex-col items-center">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
            i < current ? 'bg-blue-600 text-white' : i === current ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900/30' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
          }`}>
            {i < current ? <CheckCircleIcon className="w-4 h-4" /> : i + 1}
          </div>
          <span className={`text-xs mt-1 hidden sm:block ${i === current ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-400'}`}>
            {label}
          </span>
        </div>
        {i < STEP_LABELS.length - 1 && (
          <div className={`flex-1 h-0.5 mx-1 mb-4 sm:mb-0 ${i < current ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ─── NavRow ───────────────────────────────────────────────────────────────────

const NavRow: React.FC<{
  onBack?: () => void; backLabel?: string;
  onNext?: () => void; nextLabel?: string;
  nextDisabled?: boolean;
}> = ({ onBack, backLabel = '← Back', onNext, nextLabel = 'Next →', nextDisabled }) => (
  <div className="flex justify-between items-center pt-2">
    {onBack ? (
      <button onClick={onBack}
        className="flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        {backLabel}
      </button>
    ) : <div />}
    {onNext && (
      <button onClick={onNext} disabled={nextDisabled}
        className="flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors shadow-sm">
        {nextLabel}
      </button>
    )}
  </div>
);

// ─── PrescriptionSubmit ───────────────────────────────────────────────────────

interface PrescriptionSubmitProps {
  patient: Patient;
  doctor: DoctorInfo;
  token: string;
  alreadySubmitted: boolean;
  onBack: () => void;
  onSubmitted: (docId: string) => void;
  onExpired: () => void;
}

const PrescriptionSubmit: React.FC<PrescriptionSubmitProps> = ({
  patient, doctor, token, alreadySubmitted, onBack, onSubmitted, onExpired,
}) => {
  const [step, setStep] = useState(0);
  const [submitState, setSubmitState] = useState<SubmitState>(alreadySubmitted ? 'submitted' : 'idle');
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const today = new Date().toISOString().split('T')[0];

  const rxInitial = useMemo((): Partial<PrescriptionFormData> => ({
    drName: doctor.name,
    specialization: doctor.specialization,
    regNo: doctor.regNo,
    patientName: patient.full_name || [patient.name, patient.last_name].filter(Boolean).join(' '),
    docId: patient.doc_id,
    ageGender: [patient.age, patient.gender].filter(Boolean).join(' / '),
    issueDate: today,
    signature: doctor.signatureUrl ?? '',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const rx = usePrescriptionForm({ initialData: rxInitial, persistDraft: false });

  const patientName = patient.full_name || [patient.name, patient.last_name].filter(Boolean).join(' ') || 'Patient';

  const handleSubmit = useCallback(async () => {
    if (!rx.formData.complaintsOf.trim()) {
      setSubmitErr('Chief complaints (C/O) are required.');
      setStep(1);
      return;
    }
    if (!rx.formData.fullDiagnosis.trim()) {
      setSubmitErr('Full diagnosis is required.');
      setStep(1);
      return;
    }

    setSubmitState('submitting');
    setSubmitErr(null);

    // Ensure no required field is undefined before sending
    const payload: PrescriptionFormData = {
      ...rx.formData,
      rxLines: rx.formData.rxLines.filter(Boolean),
    };

    try {
      await submitCase(token, patient.doc_id, { prescription: payload });
      setSubmitState('submitted');
      onSubmitted(patient.doc_id);
    } catch (err: any) {
      if (err.status === 409) {
        setSubmitState('submitted');
        onSubmitted(patient.doc_id);
      } else if (err.status === 401 || err.expired) {
        onExpired();
      } else if (err.status === 403) {
        setSubmitErr("This case isn't assigned to you.");
        setSubmitState('error');
      } else {
        setSubmitErr(err.response?.data?.error || err.message || 'Submission failed. Please try again.');
        setSubmitState('error');
      }
    }
  }, [rx.formData, token, patient.doc_id, onSubmitted, onExpired]);

  return (
    <div className="space-y-5">
      {/* Back + patient name */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} aria-label="Back to cases"
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h2 className="font-bold text-gray-900 dark:text-white text-lg leading-tight">{patientName}</h2>
          <p className="text-xs text-gray-400">#{patient.doc_id}</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="px-1">
        <StepBar current={step} />
      </div>

      {/* ── Step 0: Patient Info ────────────────────────────────────────────── */}
      {step === 0 && (
        <div className="space-y-4">
          <PatientDetails patient={patient} />
          <NavRow
            onBack={onBack} backLabel="← Cases"
            onNext={() => setStep(1)} nextLabel="Fill Prescription →"
          />
        </div>
      )}

      {/* ── Step 1: Prescription Form ───────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          {/* Prescriber info bar */}
          <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-800 rounded-xl px-5 py-3 text-sm">
            <span className="font-medium text-gray-800 dark:text-gray-100">Dr. {rx.formData.drName || '—'}</span>
            {rx.formData.regNo && <span className="text-gray-500 ml-3">Reg. {rx.formData.regNo}</span>}
          </div>

          {/* Chief Complaints */}
          <Section title="Chief Complaints & Symptoms" icon={<BeakerIcon className="w-4 h-4" />} accent="emerald">
            <div className="space-y-4">
              <Field label="Chief Complaints (C/O)" required hint="Main symptoms the patient presents with">
                <textarea name="complaintsOf" value={rx.formData.complaintsOf} onChange={rx.handleChange}
                  rows={3} placeholder="e.g. Fever, headache, body ache for 3 days"
                  className={`${inputCls()} resize-none`} />
              </Field>
              <Field label="History of Present Illness (H/O)">
                <textarea name="historyOfSymptoms" value={rx.formData.historyOfSymptoms} onChange={rx.handleChange}
                  rows={3} placeholder="Onset, duration, progression of symptoms"
                  className={`${inputCls()} resize-none`} />
              </Field>
              <Field label="Intensity of Symptoms">
                <div className="flex gap-2 mt-1">
                  {(['Mild', 'Moderate', 'Severe'] as const).map((level) => (
                    <button key={level} type="button" onClick={() => rx.setField('intensityOfSymptoms', level)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        rx.formData.intensityOfSymptoms === level
                          ? level === 'Mild' ? 'bg-emerald-500 border-emerald-500 text-white'
                            : level === 'Moderate' ? 'bg-amber-500 border-amber-500 text-white'
                            : 'bg-red-500 border-red-500 text-white'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                      {level}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Concurrent Illnesses">
                <input name="concurrentIllnesses" value={rx.formData.concurrentIllnesses} onChange={rx.handleChange}
                  placeholder="Any other active conditions" className={inputCls()} />
              </Field>
            </div>
          </Section>

          {/* Medical History */}
          <Section title="Medical History" icon={<DocumentTextIcon className="w-4 h-4" />} accent="emerald">
            <div className="divide-y divide-gray-50 dark:divide-gray-800">
              <HistoryRow label="Past history of similar symptoms" value={rx.formData.pastHistoryOfSimilarSymptoms}
                onToggle={(v) => rx.setField('pastHistoryOfSimilarSymptoms', v)}
                detailName="pastHistoryDetails" detailValue={rx.formData.pastHistoryDetails}
                onDetailChange={rx.handleChange} />
              <HistoryRow label="History of surgery" value={rx.formData.historyOfSurgery}
                onToggle={(v) => rx.setField('historyOfSurgery', v)}
                detailName="surgeryDetails" detailValue={rx.formData.surgeryDetails}
                onDetailChange={rx.handleChange} detailPlaceholder="Type of surgery, year" />
              <HistoryRow label="History of travel (last 14 days)" value={rx.formData.historyOfTravel}
                onToggle={(v) => rx.setField('historyOfTravel', v)}
                detailName="travelDetails" detailValue={rx.formData.travelDetails}
                onDetailChange={rx.handleChange} detailPlaceholder="Destination(s)" />
              <HistoryRow label="Relevant family history" value={rx.formData.relevantFamilyHistory}
                onToggle={(v) => rx.setField('relevantFamilyHistory', v)}
                detailName="familyHistoryDetails" detailValue={rx.formData.familyHistoryDetails}
                onDetailChange={rx.handleChange} detailPlaceholder="Condition and relation" />
              <HistoryRow label="Substance intake (alcohol / tobacco / drugs)" value={rx.formData.substanceIntake}
                onToggle={(v) => rx.setField('substanceIntake', v)}
                detailName="substanceIntakeDetails" detailValue={rx.formData.substanceIntakeDetails}
                onDetailChange={rx.handleChange} detailPlaceholder="Substance type and frequency" />
              <HistoryRow label="Self-medication / home remedies taken" value={rx.formData.selfMedicationHomeRemedies}
                onToggle={(v) => rx.setField('selfMedicationHomeRemedies', v)}
                detailName="selfMedicationDetails" detailValue={rx.formData.selfMedicationDetails}
                onDetailChange={rx.handleChange} detailPlaceholder="Medications or remedies used" />
            </div>
            <div className="mt-4">
              <Field label="Significant Past Medical History">
                <textarea name="significantPastHistory" value={rx.formData.significantPastHistory} onChange={rx.handleChange}
                  rows={2} placeholder="Chronic conditions, major illnesses, allergies"
                  className={`${inputCls()} resize-none`} />
              </Field>
            </div>
          </Section>

          {/* Diagnosis & Rx */}
          <Section title="Diagnosis & Prescription (Rx)" icon={<ClipboardDocumentCheckIcon className="w-4 h-4" />} accent="emerald">
            <div className="space-y-5">
              <Field label="Full Diagnosis" required hint="Clinical diagnosis after examination">
                <textarea name="fullDiagnosis" value={rx.formData.fullDiagnosis} onChange={rx.handleChange}
                  rows={3} placeholder="Complete diagnosis"
                  className={`${inputCls()} resize-none`} />
              </Field>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                  Prescription Lines (Rx)
                </p>
                <div className="space-y-2">
                  {rx.formData.rxLines.map((line, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400 w-6 flex-shrink-0 text-right">
                        {idx + 1}.
                      </span>
                      <input value={line} onChange={(e) => rx.setRxLine(idx, e.target.value)}
                        placeholder="e.g. Tab Paracetamol 500mg BD × 5 days"
                        aria-label={`Prescription line ${idx + 1}`}
                        className="flex-1 px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                      <button type="button" onClick={() => rx.removeRxLine(idx)} aria-label={`Remove line ${idx + 1}`}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={rx.addRxLine}
                  className="mt-3 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-700 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                  <PlusIcon className="w-3.5 h-3.5" /> Add Rx Line
                </button>
              </div>
            </div>
          </Section>

          {/* Advice & Notes */}
          <Section title="Advice & Special Notes" icon={<DocumentTextIcon className="w-4 h-4" />} accent="emerald">
            <div className="space-y-4">
              <Field label="Advice by Doctor (RMP)">
                <textarea name="adviceByRMP" value={rx.formData.adviceByRMP} onChange={rx.handleChange}
                  rows={3} placeholder="e.g. Complete rest, plenty of fluids, follow-up after 5 days"
                  className={`${inputCls()} resize-none`} />
              </Field>
              <Field label="Special Notes / Remarks">
                <textarea name="specialNotesByRMP" value={rx.formData.specialNotesByRMP} onChange={rx.handleChange}
                  rows={3} placeholder="Any special instructions or clinical observations"
                  className={`${inputCls()} resize-none`} />
              </Field>
            </div>
          </Section>

          {submitErr && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />{submitErr}
            </div>
          )}

          <NavRow
            onBack={() => setStep(0)} backLabel="← Patient Info"
            onNext={() => { setSubmitErr(null); setStep(2); }} nextLabel="Review →"
          />
        </div>
      )}

      {/* ── Step 2: Review + Submit ──────────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          {submitState === 'submitted' ? (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-green-200 dark:border-green-800 shadow-sm px-6 py-10 text-center">
              <CheckCircleIcon className="w-14 h-14 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">Submitted Successfully</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Prescription for <strong>{patientName}</strong> has been submitted to the clinic.
              </p>
              <button onClick={onBack}
                className="mt-6 px-6 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm">
                ← Back to Cases
              </button>
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-800 px-5 py-3 border-b border-gray-100 dark:border-gray-800">
                  <h3 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-widest">
                    Submission Summary
                  </h3>
                </div>
                <dl className="px-5 py-4 space-y-2">
                  <InfoRow label="Patient" value={rx.formData.patientName} />
                  <InfoRow label="Doctor" value={rx.formData.drName} />
                  <InfoRow label="Diagnosis" value={rx.formData.fullDiagnosis} />
                  <InfoRow label="Chief Complaints" value={rx.formData.complaintsOf} />
                  <InfoRow label="Intensity" value={rx.formData.intensityOfSymptoms} />
                  {rx.formData.rxLines.filter(Boolean).length > 0 && (
                    <InfoRow label="Rx" value={rx.formData.rxLines.filter(Boolean).join(', ')} />
                  )}
                  {rx.formData.adviceByRMP && <InfoRow label="Advice" value={rx.formData.adviceByRMP} />}
                </dl>
              </div>

              {submitErr && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                  <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />{submitErr}
                </div>
              )}

              <NavRow
                onBack={() => setStep(1)} backLabel="← Edit Prescription"
                onNext={handleSubmit}
                nextLabel={submitState === 'submitting' ? 'Submitting…' : 'Submit to Clinic ✓'}
                nextDisabled={submitState === 'submitting'}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DoctorAccessPage ─────────────────────────────────────────────────────────

export const DoctorAccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [accessData, setAccessData] = useState<{
    doctor: DoctorInfo;
    patients: Patient[];
    token_minutes_remaining: number;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [submittedIds, setSubmittedIds] = useState<Set<string>>(new Set());

  const countdown = useCountdown(accessData?.token_minutes_remaining ?? null);
  const isUrgent = countdown !== null && countdown < 300;
  const isExpiredLocal = countdown !== null && countdown <= 0 && pageState === 'ready';

  useEffect(() => {
    if (!token) {
      setErrorMsg('No token found in this link.');
      setPageState('error');
      return;
    }
    getDoctorAccess(token)
      .then((res) => { setAccessData(res.data); setPageState('ready'); })
      .catch((err) => {
        if (err.expired || err.status === 401) setPageState('expired');
        else { setErrorMsg(err.response?.data?.error || err.message); setPageState('error'); }
      });
  }, [token]);

  const handleSubmitted = useCallback((docId: string) => {
    setSubmittedIds((prev) => { const next = new Set(prev); next.add(docId); return next; });
  }, []);

  const handleExpired = useCallback(() => setPageState('expired'), []);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Validating link…</p>
      </div>
    );
  }

  // ── Expired ────────────────────────────────────────────────────────────────
  if (pageState === 'expired' || isExpiredLocal) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center gap-3 px-4 text-center">
        <ClockIcon className="w-12 h-12 text-amber-400" />
        <p className="text-lg font-bold text-gray-800 dark:text-gray-200">Link Expired</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
          This link has expired — ask the clinic for a new one.
        </p>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (pageState === 'error' || !accessData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-center gap-3 px-4 text-center">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-400" />
        <p className="text-lg font-bold text-gray-800 dark:text-gray-200">Invalid Link</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
          {errorMsg || 'This link is not valid.'}
        </p>
      </div>
    );
  }

  const { doctor } = accessData;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Expiry banner */}
      <div className={`sticky top-0 z-50 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white ${isUrgent ? 'bg-red-500' : 'bg-amber-500'}`}>
        <ClockIcon className="w-4 h-4 flex-shrink-0" />
        Secure access · Expires in {countdown !== null ? fmtCountdown(countdown) : '—'}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {selectedPatient ? (
          <PrescriptionSubmit
            patient={selectedPatient}
            doctor={doctor}
            token={token}
            alreadySubmitted={submittedIds.has(selectedPatient.doc_id)}
            onBack={() => setSelectedPatient(null)}
            onSubmitted={handleSubmitted}
            onExpired={handleExpired}
          />
        ) : (
          <>
            {/* Doctor card */}
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-5 py-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <UserCircleIcon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-base font-bold text-gray-900 dark:text-white">Dr. {doctor.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {[doctor.specialization, doctor.regNo ? `Reg. ${doctor.regNo}` : ''].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>
            </div>

            {/* Cases */}
            <div>
              {/* <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Assigned Cases ({patients.length})
              </p>
              <CaseList
                patients={patients}
                submittedIds={submittedIds}
                onSelect={setSelectedPatient}
              /> */}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
